import { useEffect } from "react";
import { X } from "lucide-react";
import { IToastData } from "../../interfaces";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface INotificationProps {
  delay?: number;
  toastData: IToastData,
  onClose: () => void;
}

const Notification = ({ delay, toastData, onClose }: INotificationProps) => {
  useEffect(() => {
    if (toastData?.show) {
      const timer = setTimeout(() => {
        onClose();
      }, delay ?? 3000);
      return () => clearTimeout(timer);
    }
  }, [toastData?.show, delay, onClose]);

  if (!toastData?.show) return null;

  const getPositionClass = (position: string) => {
    switch (position) {
      case "top-start":
        return "top-0 left-0";
      case "top-center":
        return "top-0 left-1/2 -translate-x-1/2";
      case "top-end":
        return "top-0 right-0";
      case "middle-start":
        return "top-1/2 left-0 -translate-y-1/2";
      case "middle-center":
        return "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";
      case "middle-end":
        return "top-1/2 right-0 -translate-y-1/2";
      case "bottom-start":
        return "bottom-0 left-0";
      case "bottom-center":
        return "bottom-0 left-1/2 -translate-x-1/2";
      case "bottom-end":
        return "bottom-0 right-0";
      default:
        return "top-0 right-0";
    }
  };

  const getColorClass = (color: string) => {
    switch (color) {
      case "success":
        return "bg-green-500 text-white border-green-600";
      case "danger":
        return "bg-red-500 text-white border-red-600";
      case "warning":
        return "bg-yellow-500 text-white border-yellow-600";
      case "info":
        return "bg-blue-500 text-white border-blue-600";
      default:
        return "bg-gray-500 text-white border-gray-600";
    }
  };

  return (
    <div className={`fixed p-3 z-50 ${getPositionClass(toastData?.position)}`}>
      <Alert className={`${getColorClass(toastData?.color)} flex items-center justify-between min-w-[300px]`}>
        <AlertDescription className="text-white flex-1">
          {toastData?.message}
        </AlertDescription>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="ml-2 h-6 w-6 p-0 text-white hover:bg-white/20"
        >
          <X className="h-4 w-4" />
        </Button>
      </Alert>
    </div>
  );
}

export default Notification;
